import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Zap, Shield, BarChart3, Headphones, CheckCircle, Bot, DollarSign, Heart, Users, Calendar, Quote, Calculator, ClipboardCheck } from 'lucide-react'
import { FadeIn, Stagger, StaggerItem, Counter } from '@/components/animations'

export function HomePage() {
  const { t } = useTranslation()

  const services = [
    {
      title: t('services.consulting'),
      description: t('services.consultingDesc'),
      icon: BarChart3,
      href: '/services/consulting',
    },
    {
      title: t('services.development'),
      description: t('services.developmentDesc'),
      icon: Zap,
      href: '/services/development',
    },
    {
      title: t('services.platform'),
      description: t('services.platformDesc'),
      icon: Shield,
      href: '/services/platform',
    },
    {
      title: t('services.managed'),
      description: t('services.managedDesc'),
      icon: Headphones,
      href: '/services/managed-services',
    },
  ]

  const differentiators = [
    {
      title: t('differentiators.cloudNative'),
      description: t('differentiators.cloudNativeDesc'),
    },
    {
      title: t('differentiators.aiDriven'),
      description: t('differentiators.aiDrivenDesc'),
    },
    {
      title: t('differentiators.expertise'),
      description: t('differentiators.expertiseDesc'),
    },
  ]

  const stats = [
    { value: '25+', label: t('stats.issuesResolved') },
    { value: 'PCI/HIPAA', label: t('stats.tasksAutomated') },
    { value: '50+', label: t('stats.implementations') },
    { value: '100%', label: t('stats.csatRating') },
  ]

  const aiBenefits = [
    {
      title: t('home.aiBenefit1Title'),
      description: t('home.aiBenefit1Desc'),
      icon: Bot,
    },
    {
      title: t('home.aiBenefit2Title'),
      description: t('home.aiBenefit2Desc'),
      icon: DollarSign,
    },
    {
      title: t('home.aiBenefit3Title'),
      description: t('home.aiBenefit3Desc'),
      icon: Heart,
    },
    {
      title: t('home.aiBenefit4Title'),
      description: t('home.aiBenefit4Desc'),
      icon: Users,
    },
    {
      title: t('home.aiBenefit5Title'),
      description: t('home.aiBenefit5Desc'),
      icon: Calendar,
    },
  ]

  const testimonials = [
    {
      quote: t('home.testimonial1Quote'),
      author: t('home.testimonial1Author'),
      role: t('home.testimonial1Role'),
      company: t('home.testimonial1Company'),
    },
    {
      quote: t('home.testimonial2Quote'),
      author: t('home.testimonial2Author'),
      role: t('home.testimonial2Role'),
      company: t('home.testimonial2Company'),
    },
    {
      quote: t('home.testimonial3Quote'),
      author: t('home.testimonial3Author'),
      role: t('home.testimonial3Role'),
      company: t('home.testimonial3Company'),
    },
  ]

  return (
    <>
      <Helmet>
        <title>{t('meta.homeTitle')}</title>
        <meta name="description" content={t('meta.homeDescription')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-neutral-950/80">

        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />
        <div className="container-lg relative py-section-lg lg:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <FadeIn delay={0.1}>
              <p className="mb-4 text-body-md font-medium text-accent-400">
                {t('hero.tagline')}
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <h1 className="font-display text-display-lg text-white sm:text-display-xl lg:text-display-2xl">
                {t('hero.title')}{' '}
                <span className="gradient-text-primary">
                  {t('hero.titleHighlight')}
                </span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.3}>
              <p className="mx-auto mt-6 max-w-2xl text-body-lg text-neutral-400 sm:text-body-xl">
                {t('hero.subtitle')}
              </p>
            </FadeIn>
            <FadeIn delay={0.4}>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-xl gradient-primary px-6 py-3 text-body-md font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-accent-400/25"
                >
                  {t('hero.cta')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 rounded-xl border border-neutral-700 bg-neutral-800/50 px-6 py-3 text-body-md font-medium text-white backdrop-blur transition-all hover:border-accent-400/50 hover:bg-neutral-800"
                >
                  {t('hero.ctaSecondary')}
                </Link>
              </div>
            </FadeIn>
          </div>

          {/* Stats Bar */}
          <FadeIn delay={0.5} className="mx-auto mt-16 max-w-4xl">
            <div className="grid grid-cols-2 gap-8 rounded-2xl border border-neutral-800 bg-neutral-900/50 p-8 backdrop-blur lg:grid-cols-4 glow-cyan">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-display text-display-sm text-accent-400 lg:text-display-md">
                    <Counter value={stat.value} />
                  </div>
                  <div className="mt-1 text-body-sm text-neutral-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Hero Dashboard Image */}
          <FadeIn delay={0.6} className="mx-auto mt-16 max-w-5xl">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-primary-600/20 via-accent-400/20 to-vine-500/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-neutral-700/50 shadow-2xl shadow-accent-400/10">
                <img
                  src="/images/hero-dashboard.png"
                  alt="Contact Center Analytics Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        <div className="container-lg">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="text-body-md font-medium text-accent-400">{t('home.servicesTagline')}</p>
            <h2 className="mt-2 font-display text-display-sm text-white sm:text-display-md">
              {t('home.servicesTitle')}
            </h2>
            <p className="mt-4 text-body-lg text-neutral-400">
              {t('home.servicesSubtitle')}
            </p>
          </FadeIn>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" staggerDelay={0.1}>
            {services.map((service) => (
              <StaggerItem key={service.title}>
                <Link
                  to={service.href}
                  className="group block h-full rounded-xl border border-neutral-800 bg-neutral-900/50 p-6 backdrop-blur transition-all hover:border-accent-400/50 hover:bg-neutral-800/80 hover:-translate-y-1"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-primary-700 to-accent-500 text-white transition-transform group-hover:scale-110">
                    <service.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-display-xs text-white">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-body-sm text-neutral-400">
                    {service.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-body-sm font-medium text-accent-400">
                    {t('common.learnMore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Why TruVine Tech */}
      <section className="bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        <div className="container-lg">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <FadeIn>
                <p className="text-body-md font-medium text-accent-400">{t('home.whyTagline')}</p>
                <h2 className="mt-2 font-display text-display-sm text-white sm:text-display-md">
                  {t('home.whyTitle')}
                </h2>
                <p className="mt-4 text-body-lg text-neutral-400">
                  {t('home.whySubtitle')}
                </p>
              </FadeIn>
              <Stagger className="mt-8 space-y-6" staggerDelay={0.15}>
                {differentiators.map((item) => (
                  <StaggerItem key={item.title} direction="left">
                    <li className="flex gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent-400 to-vine-500 text-white">
                        <CheckCircle className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{item.title}</h3>
                        <p className="mt-1 text-body-sm text-neutral-400">{item.description}</p>
                      </div>
                    </li>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
            <FadeIn direction="right" delay={0.2}>
              <div className="relative">
                <div className="overflow-hidden rounded-2xl border border-neutral-700 shadow-2xl">
                  <img
                    src="/images/enterprise-personalized-service.png"
                    alt={t('home.whyTitle')}
                    className="w-full h-auto object-cover"
                  />
                </div>
                {/* Decorative gradient blur */}
                <div className="absolute -bottom-4 -right-4 h-32 w-32 rounded-full bg-accent-400/20 blur-3xl" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* AI Benefits Section */}
      <section className="bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        <div className="container-lg">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="text-body-md font-medium text-accent-400">{t('home.aiTagline')}</p>
            <h2 className="mt-2 font-display text-display-sm text-white sm:text-display-md">
              {t('home.aiTitle')}
            </h2>
            <p className="mt-4 text-body-lg text-neutral-400">
              {t('home.aiSubtitle')}
            </p>
          </FadeIn>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" staggerDelay={0.1}>
            {aiBenefits.map((benefit) => (
              <StaggerItem key={benefit.title}>
                <div className="group h-full rounded-xl border border-neutral-800 bg-neutral-900/50 p-6 backdrop-blur transition-all hover:border-accent-400/50 hover:bg-neutral-800/80">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-accent-500 to-vine-500 text-white transition-transform group-hover:scale-110">
                    <benefit.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-display-xs text-white">
                    {benefit.title}
                  </h3>
                  <p className="mt-2 text-body-sm text-neutral-400">
                    {benefit.description}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        <div className="container-lg">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="text-body-md font-medium text-accent-400">{t('home.testimonialsTagline')}</p>
            <h2 className="mt-2 font-display text-display-sm text-white sm:text-display-md">
              {t('home.testimonialsTitle')}
            </h2>
          </FadeIn>
          <Stagger className="mt-12 grid gap-8 lg:grid-cols-3" staggerDelay={0.15}>
            {testimonials.map((testimonial, index) => (
              <StaggerItem key={index}>
                <div className="relative h-full rounded-2xl border border-neutral-700 bg-neutral-800/50 p-8 backdrop-blur">
                  <Quote className="absolute top-6 right-6 h-8 w-8 text-accent-400/20" />
                  <p className="text-body-md text-neutral-300 leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="mt-6 border-t border-neutral-700 pt-6">
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-body-sm text-accent-400">{testimonial.role}</p>
                    <p className="text-body-sm text-neutral-500">{testimonial.company}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Partners Section */}
      <section className="bg-transparent py-section-sm lg:py-section-md backdrop-blur-sm">
        <div className="container-lg">
          <FadeIn className="text-center">
            <p className="text-body-md font-medium text-accent-400">{t('home.partnersTagline')}</p>
            <h2 className="mt-2 font-display text-display-xs text-white sm:text-display-sm">
              {t('home.partnersTitle')}
            </h2>
            <p className="mt-2 text-body-md text-neutral-400">
              {t('home.partnersSubtitle')}
            </p>
          </FadeIn>
          <FadeIn delay={0.2} className="mt-10">
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
              {/* AWS Partner Logo */}
              <div className="flex flex-col items-center gap-3 rounded-xl border border-neutral-700 bg-neutral-800/50 px-8 py-6 backdrop-blur">
                <svg className="h-10 w-auto text-[#FF9900]" viewBox="0 0 304 182" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M86.4 66.4c0 3.7.4 6.7 1.1 8.9.8 2.2 1.8 4.6 3.2 7.2.5.8.7 1.6.7 2.3 0 1-.6 2-1.9 3l-6.3 4.2c-.9.6-1.8.9-2.6.9-1 0-2-.5-3-1.4-1.4-1.5-2.6-3.1-3.6-4.7-1-1.7-2-3.6-3.1-5.9-7.8 9.2-17.6 13.8-29.4 13.8-8.4 0-15.1-2.4-20-7.2-4.9-4.8-7.4-11.2-7.4-19.2 0-8.5 3-15.4 9.1-20.6 6.1-5.2 14.2-7.8 24.5-7.8 3.4 0 6.9.3 10.6.8 3.7.5 7.5 1.3 11.5 2.2v-7.3c0-7.6-1.6-12.9-4.7-16-3.2-3.1-8.6-4.6-16.3-4.6-3.5 0-7.1.4-10.8 1.3-3.7.9-7.3 2-10.8 3.4-1.6.7-2.8 1.1-3.5 1.3-.7.2-1.2.3-1.6.3-1.4 0-2.1-1-2.1-3.1v-4.9c0-1.6.2-2.8.7-3.5.5-.7 1.4-1.4 2.8-2.1 3.5-1.8 7.7-3.3 12.6-4.5 4.9-1.3 10.1-1.9 15.6-1.9 11.9 0 20.6 2.7 26.2 8.1 5.5 5.4 8.3 13.6 8.3 24.6v32.4zM45.8 81.6c3.3 0 6.7-.6 10.3-1.8 3.6-1.2 6.8-3.4 9.5-6.4 1.6-1.9 2.8-4 3.4-6.4.6-2.4 1-5.3 1-8.7v-4.2c-2.9-.7-6-1.3-9.2-1.7-3.2-.4-6.3-.6-9.4-.6-6.7 0-11.6 1.3-14.9 4-3.3 2.7-4.9 6.5-4.9 11.5 0 4.7 1.2 8.2 3.7 10.6 2.4 2.5 5.9 3.7 10.5 3.7zm80.3 10.8c-1.8 0-3-.3-3.8-1-.8-.6-1.5-2-2.1-3.9L96.7 10.2c-.6-2-.9-3.3-.9-4 0-1.6.8-2.5 2.4-2.5h9.8c1.9 0 3.2.3 3.9 1 .8.6 1.4 2 2 3.9l16.8 66.2 15.6-66.2c.5-2 1.1-3.3 1.9-3.9.8-.6 2.2-1 4-1h8c1.9 0 3.2.3 4 1 .8.6 1.5 2 1.9 3.9l15.8 67 17.3-67c.6-2 1.3-3.3 2-3.9.8-.6 2.1-1 3.9-1h9.3c1.6 0 2.5.8 2.5 2.5 0 .5-.1 1-.2 1.6-.1.6-.3 1.4-.7 2.5l-24.1 77.3c-.6 2-1.3 3.3-2.1 3.9-.8.6-2.1 1-3.8 1h-8.6c-1.9 0-3.2-.3-4-1-.8-.7-1.5-2-1.9-4l-15.5-64.5-15.4 64.4c-.5 2-1.1 3.3-1.9 4-.8.7-2.2 1-4 1h-8.6zm128.5 2.7c-5.2 0-10.4-.6-15.4-1.8-5-1.2-8.9-2.5-11.5-4-1.6-.9-2.7-1.9-3.1-2.8-.4-.9-.6-1.9-.6-2.8v-5.1c0-2.1.8-3.1 2.3-3.1.6 0 1.2.1 1.8.3.6.2 1.5.6 2.5 1 3.4 1.5 7.1 2.7 11 3.5 4 .8 7.9 1.2 11.9 1.2 6.3 0 11.2-1.1 14.6-3.3 3.4-2.2 5.2-5.4 5.2-9.5 0-2.8-.9-5.1-2.7-7-1.8-1.9-5.2-3.6-10.1-5.2l-14.5-4.5c-7.3-2.3-12.7-5.7-16-10.2-3.3-4.4-5-9.3-5-14.5 0-4.2.9-7.9 2.7-11.1 1.8-3.2 4.2-6 7.2-8.2 3-2.3 6.4-4 10.4-5.2 4-1.2 8.2-1.7 12.6-1.7 2.2 0 4.5.1 6.7.4 2.3.3 4.4.7 6.5 1.1 2 .5 3.9 1 5.7 1.6 1.8.6 3.2 1.2 4.2 1.8 1.4.8 2.4 1.6 3 2.5.6.8.9 1.9.9 3.3v4.7c0 2.1-.8 3.2-2.3 3.2-.8 0-2.1-.4-3.8-1.2-5.7-2.6-12.1-3.9-19.2-3.9-5.7 0-10.2.9-13.3 2.8-3.1 1.9-4.7 4.8-4.7 8.9 0 2.8 1 5.2 3 7.1 2 1.9 5.7 3.8 11 5.5l14.2 4.5c7.2 2.3 12.4 5.5 15.5 9.6 3.1 4.1 4.6 8.8 4.6 14 0 4.3-.9 8.2-2.6 11.6-1.8 3.4-4.2 6.4-7.3 8.8-3.1 2.5-6.8 4.3-11.1 5.6-4.5 1.4-9.2 2.1-14.3 2.1z"/>
                  <path d="M273.5 143.7c-32.9 24.3-80.7 37.2-121.8 37.2-57.6 0-109.5-21.3-148.7-56.7-3.1-2.8-.3-6.6 3.4-4.4 42.4 24.6 94.7 39.5 148.8 39.5 36.5 0 76.6-7.6 113.5-23.2 5.5-2.5 10.2 3.6 4.8 7.6z"/>
                  <path d="M287.2 128.1c-4.2-5.4-27.8-2.6-38.5-1.3-3.2.4-3.7-2.4-.8-4.5 18.8-13.2 49.7-9.4 53.3-5 3.6 4.5-1 35.4-18.6 50.2-2.7 2.3-5.3 1.1-4.1-1.9 4-9.9 12.9-32.2 8.7-37.5z"/>
                </svg>
                <span className="text-body-sm font-medium text-neutral-300">AWS Partner</span>
              </div>
              {/* Salesforce Partner Logo */}
              <div className="flex flex-col items-center gap-3 rounded-xl border border-neutral-700 bg-neutral-800/50 px-8 py-6 backdrop-blur">
                <svg className="h-10 w-auto text-[#00A1E0]" viewBox="0 0 100 70" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M41.5 14.5c3.7-3.9 8.9-6.3 14.7-6.3 8.5 0 15.8 5.2 18.9 12.6 2.7-1.2 5.7-1.9 8.9-1.9 12.1 0 22 9.8 22 22s-9.8 22-22 22c-1.9 0-3.8-.2-5.6-.7-2.8 5.6-8.6 9.5-15.4 9.5-3.3 0-6.3-.9-8.9-2.5-2.9 6.6-9.5 11.2-17.2 11.2-7.5 0-14-4.4-17-10.8C10.7 67.6 2 58.1 2 46.7c0-12.5 10.1-22.6 22.6-22.6 5.4 0 10.4 1.9 14.3 5.1.8-5.4 2.6-10.3 5.6-14.7h-3z"/>
                </svg>
                <span className="text-body-sm font-medium text-neutral-300">Salesforce Partner</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Interactive Tools Section */}
      <section className="bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        <div className="container-lg">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="text-body-md font-medium text-accent-400">{t('home.toolsTagline')}</p>
            <h2 className="mt-2 font-display text-display-sm text-white sm:text-display-md">
              {t('home.toolsTitle')}
            </h2>
            <p className="mt-4 text-body-lg text-neutral-400">
              {t('home.toolsSubtitle')}
            </p>
          </FadeIn>
          <Stagger className="mt-12 grid gap-8 md:grid-cols-2" staggerDelay={0.15}>
            <StaggerItem>
              <Link
                to="/tools/roi-calculator"
                className="group block h-full rounded-2xl border border-neutral-700 bg-gradient-to-br from-neutral-800/80 to-neutral-900/80 p-8 backdrop-blur transition-all hover:border-accent-400/50 hover:-translate-y-1"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-accent-500 text-white transition-transform group-hover:scale-110">
                  <Calculator className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display text-display-xs text-white">
                  {t('home.toolRoiTitle')}
                </h3>
                <p className="mt-3 text-body-md text-neutral-400">
                  {t('home.toolRoiDesc')}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-body-md font-semibold text-accent-400">
                  {t('home.toolRoiCta')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                to="/tools/assessment"
                className="group block h-full rounded-2xl border border-neutral-700 bg-gradient-to-br from-neutral-800/80 to-neutral-900/80 p-8 backdrop-blur transition-all hover:border-accent-400/50 hover:-translate-y-1"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-vine-500 to-accent-500 text-white transition-transform group-hover:scale-110">
                  <ClipboardCheck className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display text-display-xs text-white">
                  {t('home.toolAssessmentTitle')}
                </h3>
                <p className="mt-3 text-body-md text-neutral-400">
                  {t('home.toolAssessmentDesc')}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-body-md font-semibold text-accent-400">
                  {t('home.toolAssessmentCta')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-transparent py-section-md lg:py-section-lg backdrop-blur-sm">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/50 via-neutral-950 to-accent-900/30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-primary-700/10 blur-[150px]" />

        <div className="container-lg relative text-center">
          <FadeIn>
            <h2 className="font-display text-display-sm text-white sm:text-display-md">
              {t('home.ctaTitle')}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mx-auto mt-4 max-w-2xl text-body-lg text-neutral-400">
              {t('home.ctaSubtitle')}
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-10">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-xl gradient-primary px-8 py-4 text-body-lg font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-accent-400/30"
              >
                {t('home.ctaButton')}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  )
}
